// Libraries
import Helmet from "react-helmet";
import { Flex, Image, Link, Box, Button } from "rebass";
import { gql } from "@apollo/client";
import client from "../../lib/apollo-client.js";

// Config
import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();
const { SITE_NAME } = publicRuntimeConfig;

// Component Imports
import {
	LayoutPadding,
	HeroStripe,
	Layout,
	ListView,
	Text,
	Loading,
	RenderStreamField,
} from "../../components";
import { colors } from "../../styleguide/theme";
import { board_members } from "../../mocks/board-members";
import { useEffect, useState } from "react";
import { image } from "faker";

const HeroSection = ({ sectionTitle, subtitle }) => (
	<HeroStripe bg={colors.green}>
		<LayoutPadding py={[6, 6, 8]}>
			<Flex
				width={"100%"}
				flexDirection={["column", "row", "row", "row"]}
				color={colors.white}
				pb={[4, 6, 8, 10]}
			>
				<Flex mr={[0, 9, 11]} mb={[5, 0]}>
					<Text as="h1" fontSize={[5]}>
						{sectionTitle}
					</Text>
				</Flex>
				<Text as="h3">{subtitle}</Text>
			</Flex>
		</LayoutPadding>
	</HeroStripe>
);

const MemberCard = ({
	image,
	firstName,
	lastName,
	title,
	email,
	linkedinUrl,
	biography,
	personalUrl,
	setCurrentBiography,
	setModalOpen,
}) => (
	<Flex
		width={["100%"]}
		flexDirection={"column"}
		// alignItems={'center'}
		css={`
			min-height: 425px;
		`}
	>
		<Flex
			width={"100%"}
			css={`
				overflow: hidden;
				position: relative;
				height: 350px;
			`}
		>
			<Box
				css={`
					width: 100%;
					height: 100%;
					background-image: url("${image && image.rendition.url}");
					background-repeat: no-repeat;
					background-size: cover;
				`}
				alt={`Board member: ${firstName} ${lastName}`}
			/>
		</Flex>
		<Flex flexDirection={"column"} px={[7, 0]}>
			<Text as="h5" mt={[3, 3, 5]} mb={[1, 1, 3]}>
				{`${firstName} ${lastName}`}
			</Text>
			<Text as="p">{title}</Text>
			{email && (
				<Flex mt={(0, 0, 1)} alignItems={"center"}>
					<Link
						href={`mailto:${email}`}
						css={`
							display: flex;
							align-items: center;
						`}
					>
						<Image
							mr={3}
							width={"1rem"}
							src="/static/icons/misc/Email-Icon_Blue.svg"
						/>
						<Text
							as="span"
							color={colors.lightblue}
							styling={`
                display: inline-block; 
                text-decoration: underline;
            `}
						>
							{email}
						</Text>
					</Link>
				</Flex>
			)}
			{linkedinUrl && (
				<Flex mt={(0, 0, 1)} alignItems={"center"}>
					<Link
						href={`${linkedinUrl}`}
						css={`
							display: flex;
							align-items: center;
						`}
					>
						<Image
							mr={3}
							width={"1rem"}
							src="/static/icons/social/linkedIn_blue.svg"
						/>
						<Text
							as="span"
							color={colors.lightblue}
							styling={`
                display: inline-block; 
                text-decoration: underline;
            `}
						>
							{/* {linkedinUrl} */}
							LinkedIn Profile
						</Text>
					</Link>
				</Flex>
			)}
			{biography && (
				<Button
					style={{
						border: "1px solid black",
						color: "black",
						backgroundColor: "transparent",
						width: "90px",
						fontSize: "12px",
						fontWeight: "100",
						marginTop: "20px",
					}}
					onClick={() => {
						setCurrentBiography({
							biography,
							title,
							firstName,
							lastName,
							image,
						});
						setModalOpen(true);
					}}
				>
					View More
				</Button>
			)}
			{personalUrl && (
				<Flex mt={[1]}>
					<a
						target="_blank"
						href={personalUrl}
						style={{ fontStyle: "italic", color: colors.black }}
					>
						<Text as="p">Visit Personal Site</Text>
					</a>
				</Flex>
			)}
		</Flex>
	</Flex>
);

const CardSection = ({ members, setCurrentBiography, setModalOpen }) => {
	return (
		<Flex px={[0, 5, 11]} py={[0, 7, 9]} justifyContent={"center"}>
			<ListView
				autoFillWidth={"255px"}
				gap={"3.5"}
				columnsTablet={2}
				gapTablet={1}
				columnsMobile={1}
				gapMobile={0}
			>
				{members.map((member) => {
					return (
						<MemberCard
							key={member.node.id}
							setModalOpen={setModalOpen}
							setCurrentBiography={setCurrentBiography}
							{...member.node}
						/>
					);
				})}
			</ListView>
		</Flex>
	);
};

const ContentSection = ({
	recuitmentTitle,
	recuitmentSubtitle,
	recuitmentDescription,
}) => (
	<Flex
		width={"100%"}
		flexDirection={"column"}
		bg={colors.lightblue}
		color={colors.white}
	>
		<LayoutPadding py={[9, 9, 11]}>
			<Flex
				width={"100%"}
				flexDirection={["column", "column", "column", "row"]}
			>
				<Flex>
					<Text as="h2" mr={[0, 0, 0, 10]}>
						{recuitmentTitle}
					</Text>
				</Flex>
				<Text as="h3" mt={[4, 4, 4, 0]} pl={[0, 0, 0, 8]}>
					{recuitmentSubtitle}
				</Text>
			</Flex>
			<Flex py={[3, 3, 8]} width={"100%"}>
				<Text
					as="p"
					styling={`
              line-height: 22px;
              column-count: 2;
              column-gap: 3.5rem;

              @media only screen and (max-width: 768px) {
                  column-count: 1;
              }
          `}
				>
					{recuitmentDescription}
				</Text>
			</Flex>
		</LayoutPadding>
	</Flex>
);

const PastMembers = ({ body }) => (
	<Flex pt={[0, 7, 9]} flexDirection={"column"}>
		<RenderStreamField body={body} />
	</Flex>
);

const GET_BOARD_INFO = gql`
	query getBoardMembersPage {
		boardIndex {
			edges {
				node {
					title
					tabDescription
					searchDescription
					subtitle
					sectionTitle
					members(orderBy: "last_name") {
						edges {
							node {
								id
								firstName
								lastName
								title
								email
								linkedinUrl
								image {
									rendition(format: "jpeg", fill: "600x900") {
										url
									}
								}
								biography
								personalUrl
							}
						}
					}
					executiveMembers(orderBy: "ranking") {
						edges {
							node {
								id
								firstName
								lastName
								title
								email
								linkedinUrl
								image {
									rendition(format: "jpeg", fill: "600x900") {
										url
									}
								}
								biography
								personalUrl
							}
						}
					}
					recuitmentTitle
					recuitmentSubtitle
					recuitmentDescription
					pastMembers
				}
			}
		}
	}
`;

const Index = ({ error, data }) => {
	const {
		title,
		tabDescription,
		searchDescription,
		subtitle,
		sectionTitle,
		members,
		executiveMembers,
		recuitmentTitle,
		recuitmentSubtitle,
		recuitmentDescription,
		pastMembers,
	} = data.boardIndex.edges[0].node;
	const [currentBiography, setCurrentBiography] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);

	useEffect(() => {
		const callback = function (event) {
			if (event.key === "Escape") {
				setModalOpen(false);
			}
		};

		if (process.browser) {
			console.log("runs");
			const listener = document.addEventListener("keydown", callback);
		}
		return () => {
			document.removeEventListener("keydown", callback);
		};
	});

	const allMembers = [...executiveMembers.edges, ...members.edges];

	return (
		<React.Fragment>
			<Layout {...{ title, tabDescription, searchDescription }}>
				<HeroSection {...{ sectionTitle, subtitle }} />
				<PastMembers body={pastMembers} />
				<CardSection
					members={allMembers}
					setModalOpen={setModalOpen}
					setCurrentBiography={setCurrentBiography}
				/>
				<ContentSection
					{...{
						recuitmentTitle,
						recuitmentSubtitle,
						recuitmentDescription,
					}}
				/>
			</Layout>
			{process.browser && modalOpen && (
				<>
					{/* Blackout */}
					<Box
						onClick={() => modalOpen && setModalOpen(false)}
						width={"100%"}
						height={"100%"}
						style={{
							position: "fixed",
							top: "0",
							left: 0,
							background: "rgba(0, 0, 0, 0.7)",
							zIndex: 10,
						}}
					/>
					{/* Modal */}
					<Box
						tabindex="-1"
						role="dialog"
						aria-labelledby="dialogue-title"
						width={["95%", "80%", "80%", "50%"]}
						height={"80%"}
						overflowY={"auto"}
						style={{
							position: "fixed",
							borderRadius: 12,
							background: "white",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							zIndex: 10,
							border: "2px solid black",
							padding: 20,
						}}
					>
						<Flex width={"100%"} justifyContent={"flex-end"}>
							<Button
								mr={2}
								mt={2}
								onClick={() => setModalOpen(false)}
								style={{
									background: "white",
									color: "black",
									fontSize: 22,
								}}
							>
								X
							</Button>
						</Flex>
						<Flex
							flexDirection={"column"}
							alignItems={"center"}
							justifyContent={"center"}
							paddingTop={10}
						>
							<Image
								width={["180px"]}
								mb={10}
								src={currentBiography.image.rendition.url}
							/>
							<Text as="h1" id="dialogue-title" mb={2}>
								{currentBiography.firstName +
									" " +
									currentBiography.lastName}
							</Text>
							<Text as="p" mb={4}>
								{title}
							</Text>
							<Box
								width={["90%", "80%"]}
								maxWidth={[900]}
								dangerouslySetInnerHTML={{
									__html: currentBiography.biography,
								}}
							/>
						</Flex>
					</Box>
				</>
			)}
		</React.Fragment>
	);
};

export async function getServerSideProps() {
	const { data, error } = await client.query({
		query: GET_BOARD_INFO,
	});

	return {
		props: {
			data: error ? {} : data,
			error: error ? true : false,
		},
	};
}

export default Index;
